import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["balanceBefore", "balanceAfter", "amount"]
  static values = {
    balanceBeforeCents: Number,
    balanceEffect: String,
    outstandingBalanceCents: { type: Number, default: 0 }
  }

  connect() {
    this.modalElement = this.element.closest(".modal")
    this.removeDuplicateModal()
    this.cleanupModalArtifacts()
    this.prefillOutstandingBalance()
    this.updateBalance()

    this.handleSubmitEnd = this.onSubmitEnd.bind(this)
    this.handleModalHidden = this.cleanupModalArtifacts.bind(this)

    this.element.addEventListener("turbo:submit-end", this.handleSubmitEnd)
    this.modalElement?.addEventListener("hidden.bs.modal", this.handleModalHidden)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.handleSubmitEnd)
    this.modalElement?.removeEventListener("hidden.bs.modal", this.handleModalHidden)
  }

  onSubmitEnd(event) {
    if (!event.detail.success) return

    this.hideModal()
  }

  hideModal() {
    const modal = this.modalElement
    if (!modal) return

    const instance = Modal.getInstance(modal)
    if (instance) {
      instance.hide()
      return
    }

    this.cleanupModalArtifacts()
  }

  removeDuplicateModal() {
    const modal = this.modalElement
    if (!modal?.id) return

    document.querySelectorAll(`#${CSS.escape(modal.id)}`).forEach((element) => {
      if (element !== modal) element.remove()
    })
  }

  cleanupModalArtifacts() {
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())

    document.querySelectorAll(".turbo-closed-modal.show").forEach((modal) => {
      modal.classList.remove("show")
      modal.style.display = "none"
      modal.setAttribute("aria-hidden", "true")
    })
  }

  updateBalance() {
    const amountCents = this.parseAmountCents()
    let afterCents = this.balanceBeforeCentsValue

    if (amountCents !== 0) {
      const absoluteCents = Math.abs(amountCents)
      const increasesBalance = amountCents > 0 ? this.balanceEffectValue === "debit" : this.balanceEffectValue === "credit"

      if (increasesBalance) {
        afterCents += absoluteCents
      } else {
        afterCents -= absoluteCents
      }
    }

    this.balanceAfterTarget.textContent = this.formatCustomerBalance(afterCents)
  }

  fillOutstandingBalance() {
    if (this.outstandingBalanceCentsValue <= 0) return

    this.amountTarget.value = this.formatAmountInput(this.outstandingBalanceCentsValue)
    this.updateBalance()
  }

  prefillOutstandingBalance() {
    if (this.outstandingBalanceCentsValue <= 0) return
    if (this.amountTarget.value.trim() !== "") return

    this.fillOutstandingBalance()
  }

  formatAmountInput(cents) {
    return (cents / 100).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  parseAmountCents() {
    const raw = this.amountTarget.value.trim().replace(/[€\s\u00A0]/g, "").replace(",", ".")
    if (raw === "") return 0

    const value = Number.parseFloat(raw)
    if (Number.isNaN(value) || value === 0) return 0

    return Math.round(value * 100)
  }

  formatCustomerBalance(balanceCents) {
    const customerCents = balanceCents === 0 ? 0 : -balanceCents

    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(customerCents / 100)
  }
}
